import { useEffect, useMemo, useState, type Dispatch, type SetStateAction } from 'react';
import {
  projectEssentialApi,
  type FilterOptions,
  type ProjectFilters,
} from '../services/api';
import type { AppliedFilterChip } from '../components/PageSearchBar';

/**
 * Builds the grouped `appliedFilters` chip list (Project / Region /
 * District / Property Type) consumed by `PageSearchBar`, plus a
 * `clearAll` helper. Centralises chip-label formatting (district
 * number → friendly name, property-type id → label, project id →
 * name) and per-chip removal so every page that wires
 * `ProjectFiltersDialog` renders the same chip semantics.
 */
export function useAppliedFilterChips(
  filters: ProjectFilters,
  setFilters: Dispatch<SetStateAction<ProjectFilters>>,
): { chips: AppliedFilterChip[]; clearAll: () => void } {
  const [filterOptions, setFilterOptions] = useState<FilterOptions | null>(null);
  useEffect(() => {
    if (filterOptions) return;
    let mounted = true;
    projectEssentialApi
      .getFilterOptions()
      .then((res) => {
        if (mounted) setFilterOptions(res);
      })
      .catch(() => {
        // Non-fatal: chips fall back to raw values below.
      });
    return () => {
      mounted = false;
    };
  }, [filterOptions]);

  // Project names for the "Project" chip group — resi + commercial +
  // international pool (same as the filter dialog dropdown).
  const [projectNameById, setProjectNameById] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!(filters.projectIds && filters.projectIds.length > 0)) return;
    let mounted = true;
    projectEssentialApi
      .getFilterProjectPool()
      .then((items) => {
        if (!mounted) return;
        const map: Record<string, string> = {};
        for (const p of items) map[p.id] = p.name || p.id;
        setProjectNameById(map);
      })
      .catch(() => {
        // Non-fatal: project chips fall back to the raw id below.
      });
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- re-fetch only when the selection identity changes
  }, [filters.projectIds?.join(',')]);

  const chips = useMemo<AppliedFilterChip[]>(() => {
    const list: AppliedFilterChip[] = [];

    (filters.projectIds ?? []).forEach((id) => {
      list.push({
        id: `project:${id}`,
        group: 'Project',
        label: projectNameById[id] || id,
        onRemove: () =>
          setFilters((f) => ({
            ...f,
            projectIds: (f.projectIds ?? []).filter((x) => x !== id),
          })),
      });
    });

    (filters.regions ?? []).forEach((r) => {
      list.push({
        id: `region:${r}`,
        group: 'Region',
        label: r,
        onRemove: () =>
          setFilters((f) => ({ ...f, regions: (f.regions ?? []).filter((x) => x !== r) })),
      });
    });

    (filters.districts ?? []).forEach((no) => {
      const meta = filterOptions?.districts.find((d) => d.no === no);
      // `District.Name` already includes the formatted code prefix
      // (e.g. `D01 - City Hall`), so use it directly. Fall back to a
      // normalized `D01` form when no friendly name is available.
      let label: string;
      if (meta?.name && meta.name !== `District ${no}`) {
        label = meta.name;
      } else {
        const cleaned = no.replace(/^D+/i, '').trim();
        label = /^\d+$/.test(cleaned) ? `D${cleaned.padStart(2, '0')}` : `D${cleaned}`;
      }
      list.push({
        id: `district:${no}`,
        group: 'District',
        label,
        onRemove: () =>
          setFilters((f) => ({
            ...f,
            districts: (f.districts ?? []).filter((x) => x !== no),
          })),
      });
    });

    (filters.propertyTypes ?? []).forEach((id) => {
      const meta = filterOptions?.propertyTypes.find((p) => p.id === id);
      list.push({
        id: `type:${id}`,
        group: 'Property Type',
        label: meta?.label ?? id,
        onRemove: () =>
          setFilters((f) => ({
            ...f,
            propertyTypes: (f.propertyTypes ?? []).filter((x) => x !== id),
          })),
      });
    });

    if (filters.soleMarketing) {
      list.push({
        id: 'soleMarketing',
        group: 'Marketing',
        label: 'Sole Marketing',
        onRemove: () => setFilters((f) => ({ ...f, soleMarketing: undefined })),
      });
    }

    return list;
  }, [filters, filterOptions, projectNameById, setFilters]);

  const clearAll = () => setFilters({});

  return { chips, clearAll };
}
